import { Input, Modal, Row } from 'antd';
import React, { useEffect, useState } from 'react';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
} from '@ant-design/icons';

import { translate } from '@portal/services/i18n';
import { validateSmsCode } from '@portal/utils/validate';
import { transfer } from '@portal/config/app.json';

interface ConfirmationModalProps {
  mode?: 'text' | 'number';
  visible: boolean;
  setVisible: (visible: boolean) => void;
  handleConfirmation: (pin: string) => void;
  title?: string;
  text?: string;
  buttonText?: string;
  isSms?: boolean;
  resend?: () => void;
  inputId?: string;
  buttonId?: string;
}

const ConfirmationModal = ({
  handleConfirmation,
  setVisible,
  visible,
  title,
  text,
  mode = 'text',
  buttonText = 'Confirmar',
  isSms = false,
  resend,
  inputId = '',
  buttonId = '',
}: ConfirmationModalProps) => {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  const handlePin = (digit: string) => {
    if (pin.length === 6) {
      return;
    }
    setPin(`${pin}${digit}`);
  };

  const deletePin = () => {
    if (pin.length > 0) {
      const value = pin.slice(0, -1);
      setPin(value);
    }
  };

  const handleClean = () => {
    setPin('');
  };

  useEffect(() => {
    if (visible) {
      setPin('');
    }
  }, [setPin, visible]);

  return (
    <Modal
      open={visible}
      footer={null}
      title={null}
      className="confirmation-modal"
      closable
      onCancel={() => setVisible(false)}
      width={480}
      zIndex={300}
    >
      <div className="confirmation-modal__header">
        {!title ? (
          <ThemedText
            variant="h2"
            fontWeight="bold"
            className="confirmation-modal__title"
          >
            {translate('COMPONENTS.CONFIRMATION_MODAL.TITLE')}
          </ThemedText>
        ) : (
          <ThemedText
            variant="h2"
            fontWeight="bold"
            className="confirmation-modal__title"
          >
            {title}
          </ThemedText>
        )}
        {!text ? (
          <ThemedText
            variant="body"
            color="gray"
            className="confirmation-modal__instruction"
          >
            {translate('COMPONENTS.CONFIRMATION_MODAL.TEXT')}
          </ThemedText>
        ) : (
          <ThemedText
            variant="body"
            color="gray"
            className="confirmation-modal__instruction"
          >
            {text}
          </ThemedText>
        )}
      </div>
      <Row align="middle" justify="end">
        <ThemedText
          variant="bodySmall"
          color="gray"
          className="confirmation-modal__clean"
          onClick={handleClean}
          style={{ cursor: 'pointer' }}
        >
          {translate('COMPONENTS.CONFIRMATION_MODAL.CLEAN')}
        </ThemedText>
      </Row>
      <div className="confirmation-modal__input-container" data-sentry-mask>
        <Input
          {...{ testId: inputId }}
          prefix={
            <div className="confirmation-modal__lock-icon">
              <LockOutlined />
            </div>
          }
          value={pin}
          disabled={!isSms}
          maxLength={6}
          type={showPin ? 'text' : 'password'}
          addonAfter={
            !showPin ? (
              <EyeInvisibleOutlined onClick={() => setShowPin(!showPin)} />
            ) : (
              <EyeOutlined onClick={() => setShowPin(!showPin)} />
            )
          }
          onChange={(event) => {
            if (!isSms) return;
            setPin(
              validateSmsCode(
                mode === 'number'
                  ? `${event.target.value}`.replace(/\D/g, '')
                  : event.target.value
              )
            );
          }}
        />
      </div>
      {!isSms && (
        <div className="confirmation-modal__number-grid">
          {digits.map((num) => (
            <p
              key={num}
              className="confirmation-modal__number-grid__digit"
              onClick={() => handlePin(num)}
            >
              {num}
            </p>
          ))}
          <p className="confirmation-modal__number-grid__digit-hidden">0</p>
          <p
            className="confirmation-modal__number-grid__digit"
            onClick={() => handlePin('0')}
          >
            0
          </p>
          <p
            className="confirmation-modal__number-grid__digit"
            onClick={deletePin}
          >
            <span>
              <img src="/assets/svg/ic_backspace.svg" alt="backspace" />
            </span>
          </p>
        </div>
      )}
      {isSms && transfer.hasSmsConfirmation ? (
        <div className="confirmation-modal__resend-container">
          <ThemedText
            variant="bodySmall"
            color="primary"
            fontWeight="medium"
            onClick={(event) => {
              event?.preventDefault();
              resend?.();
            }}
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {translate('COMPONENTS.CONFIRMATION_MODAL.RESEND')}
          </ThemedText>
        </div>
      ) : null}
      <div className="confirmation-modal__button-container">
        <AdvancedButton
          testId={buttonId}
          fullWidth
          text={buttonText}
          onClick={() => handleConfirmation(pin)}
          disabled={pin.length < 6}
        />
      </div>
    </Modal>
  );
};

export default ConfirmationModal;
